import { useCallback, useEffect, useRef, useState } from 'react';
import { View } from 'react-native';
import { Flex, Typography, useScreenSize } from '@banhala/ui-kit';
import {
  type NavigationProp,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import type {
  ThreeColFilterProps,
  ThreeColFilterItemWithLogging,
} from './types';
import { useLogAnalyticsEvent } from '../../../../hooks/useLogAnalyticsEvent';
import { useFilterListAtom } from '../FilterList/Provider/filterList/useFilterListAtom';

export const ThreeColFilter = ({
  body,
  logging,
  scrollToFilterList,
}: ThreeColFilterProps) => {
  const navigation = useNavigation<
    NavigationProp<{
      [route: string]: Record<string, string[] | undefined>;
    }>
  >();
  const { params: routeParams } = useRoute();
  const { setFilterListAtom } = useFilterListAtom();
  const logAnalyticsEvent = useLogAnalyticsEvent();
  const { width: screenWidth } = useScreenSize();
  const [selectedValue, setSelectedValue] = useState(
    body?.selected_value ?? '0',
  );

  // 이 필터의 param 이 navigation 에서 사라지면 선택 상태도 해제한다.
  // (라디오 등 다른 필터가 적용될 때 이 필터 param 이 제거되는데, selectedValue 는 로컬
  //  state 라 그대로 남아 "적용되지 않은 값이 선택돼 보이는" 상태가 된다.)
  const paramValue = body?.query_name
    ? (routeParams as Record<string, unknown> | undefined)?.[body.query_name]
    : undefined;
  // 최초 마운트처럼 "애초에 param 이 없던" 경우까지 초기화하면, param 없이 서버가
  // selected_value 를 내려주는 케이스를 덮어쓴다. 있음→없음 전이에서만 해제한다.
  const hadParamRef = useRef(paramValue != null);
  useEffect(() => {
    const hasParam = paramValue != null;
    const removed = hadParamRef.current && !hasParam;
    hadParamRef.current = hasParam;
    if (removed) {
      setSelectedValue('0');
    }
  }, [paramValue]);

  console.log('selectedValue', selectedValue, body?.query_name);

  const handleSelect = useCallback(
    (item: ThreeColFilterItemWithLogging) => {
      logAnalyticsEvent({
        name: 'CLICK_FILTER',
        analytics: { ...logging?.analytics, ...item.logging?.analytics },
        ably_only_log: {
          ...logging?.ably_only_log,
          ...item.logging?.ably_only_log,
        },
      });

      setSelectedValue(item.item.query_value);
      scrollToFilterList();

      if (body?.query_name) {
        setFilterListAtom(prev => ({
          ...prev,
          [body.query_name]: [item.item.query_value],
        }));
        navigation.setParams({
          [body.query_name]: [item.item.query_value],
        });
      }
    },
    [
      logging,
      body,
      scrollToFilterList,
      setFilterListAtom,
      navigation,
      logAnalyticsEvent,
    ],
  );

  if (!body || body.possible_items.length === 0) return null;

  const cellWidth = Math.floor(screenWidth / 3);

  return (
    <Flex borderTopWidth={1} borderColor="border_tertiary">
      <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
        {body.possible_items.map((item, index) => {
          const isSelected = item.item.query_value === selectedValue;
          const col = index % 3;
          return (
            <Flex
              key={item.item.query_value}
              width={cellWidth}
              height={43}
              justifyContent="center"
              padding={{ horizontal: 16 }}
              borderBottomWidth={1}
              borderColor="border_tertiary"
              {...(col < 2 && { borderRightWidth: 1 })}
              onPress={() => handleSelect(item)}
            >
              <Typography
                type={isSelected ? 'subtitle2' : 'body2'}
                color={isSelected ? 'content_primary' : 'content_tertiary'}
              >
                {item.item.text}
              </Typography>
            </Flex>
          );
        })}
      </View>
    </Flex>
  );
};
